import axios from "axios";
import { createContext } from "react";
import { getMediaUrl } from "../functions/apiFunctions/mediaUrls";
import { WPMedia } from "../wordpress-package";

export type getImageMediaObject = (ids: number) => Promise<WPMedia | null>;
export interface ImageContextType {
  getImageMediaObject: getImageMediaObject;
  getImageUrl: (imageId: number) => Promise<string>;
}

const blobUrls = new Map<string, string>();
const preloadedUrls = new Set<string>();
const inFlightBlobRequests = new Map<string, Promise<string>>();

function preload(mediaUrl: string) {
  if (preloadedUrls.has(mediaUrl)) return;
  preloadedUrls.add(mediaUrl);
  const image = new Image();
  image.src = mediaUrl;
}

export async function getImageUrl(imageId: number): Promise<string> {
  const mediaUrl = await getMediaUrl(imageId);
  if (mediaUrl === "") return mediaUrl;

  if (import.meta.env.VITE_CACHE_IMAGES !== "true") {
    preload(mediaUrl);
    return mediaUrl;
  }

  const cachedBlobUrl = blobUrls.get(mediaUrl);
  if (cachedBlobUrl !== undefined) return cachedBlobUrl;

  const inFlightRequest = inFlightBlobRequests.get(mediaUrl);
  if (inFlightRequest !== undefined) return inFlightRequest;

  const request = fetchBlobUrl(mediaUrl).finally(() => inFlightBlobRequests.delete(mediaUrl));
  inFlightBlobRequests.set(mediaUrl, request);
  return request;
}

async function fetchBlobUrl(mediaUrl: string): Promise<string> {
  try {
    const result = await axios.get(mediaUrl, { responseType: "arraybuffer" });
    const blob = new Blob([result.data], { type: result.headers["content-type"] });
    const blobUrl = window.URL.createObjectURL(blob);
    blobUrls.set(mediaUrl, blobUrl);
    return blobUrl;
  } catch {
    return mediaUrl;
  }
}

async function getImageMediaObject(imageId: number): Promise<WPMedia | null> {
  const sourceUrl = await getMediaUrl(imageId);
  if (sourceUrl === "") return null;
  return { id: imageId, source_url: sourceUrl } as WPMedia;
}

export const imageContextValue: ImageContextType = {
  getImageMediaObject,
  getImageUrl
};

export const ImageContext = createContext<ImageContextType>(imageContextValue);
